import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";

export default function AddCustomerDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [form, setForm] = useState({
    mobile: "",
    otp: "",
    name: "",
    license_number: "",
    pos_id: "",
    address: "",
    password: "",
  });

  const reset = () => {
    setStep(1);
    setSaving(false);
    setDemoOtp("");
    setForm({
      mobile: "",
      otp: "",
      name: "",
      license_number: "",
      pos_id: "",
      address: "",
      password: "",
    });
  };

  const close = () => {
    reset();
    onClose();
  };

  const sendOtp = async () => {
    if (form.mobile.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }

    try {
      setSaving(true);
      const { data } = await api.post("/admin/customers/request-otp", {
        mobile: form.mobile,
      });
      setDemoOtp(data.demo_otp || "");
      setStep(2);
      toast.success(
        data.demo_otp
          ? `OTP sent. Demo OTP: ${data.demo_otp}`
          : "OTP sent successfully",
      );
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const createCustomer = async () => {
    if (
      !form.otp ||
      !form.name.trim() ||
      !form.license_number.trim() ||
      !form.pos_id.trim() ||
      !form.address.trim()
    ) {
      toast.error("Please complete all required fields");
      return;
    }

    try {
      setSaving(true);
      let uploadedPhotoUrl = photoUrl;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const upload = await api.post("/admin/upload-customer-photo", fd);
        uploadedPhotoUrl = upload.data.url || "";
        setPhotoUrl(uploadedPhotoUrl);
      }
      const payload = { ...form, photo_url: uploadedPhotoUrl || null };
      const { data } = await api.post("/admin/customers", payload);
      toast.success(`Customer created: ${data.user.customer_code}`);
      onCreated?.(data.user);
      close();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="sf-label">Mobile Number *</label>
              <input
                className="sf-input w-full"
                value={form.mobile}
                maxLength={10}
                inputMode="numeric"
                placeholder="10-digit mobile number"
                onChange={(e) =>
                  setForm({
                    ...form,
                    mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                  })
                }
              />
            </div>

            <button
              className="sf-btn-primary w-full"
              onClick={sendOtp}
              disabled={saving}
            >
              {saving ? "Sending OTP..." : "Send OTP"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-3 text-sm">
              OTP sent to <b>+91 {form.mobile}</b>
              {demoOtp && (
                <span className="ml-2 font-semibold text-emerald-700">
                  Demo OTP: {demoOtp}
                </span>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="sf-label">OTP *</label>
                <input
                  className="sf-input w-full text-center text-2xl font-bold tracking-[0.7em]"
                  value={form.otp}
                  maxLength={6}
                  placeholder="••••••"
                  inputMode="numeric"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      otp: e.target.value.replace(/\D/g, "").slice(0, 6),
                    })
                  }
                />
              </div>

              <div>
                <label className="sf-label">Customer Name *</label>
                <input
                  className="sf-input w-full"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div>
                <label className="sf-label">Licence Number *</label>
                <input
                  className="sf-input w-full"
                  value={form.license_number}
                  onChange={(e) =>
                    setForm({ ...form, license_number: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="sf-label">POS ID *</label>
                <input
                  className="sf-input w-full"
                  value={form.pos_id}
                  onChange={(e) => setForm({ ...form, pos_id: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="sf-label">Address *</label>
                <textarea
                  className="sf-input min-h-24 w-full"
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </div>

              <div className="sm:col-span-2">
              <div className="sm:col-span-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                <label className="sf-label">Customer Photo / Document</label>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="sf-input w-full" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                <p className="mt-1 text-xs text-slate-500">JPG, PNG or WEBP · Maximum 15 MB</p>
                {photoFile && <p className="mt-1 text-xs font-medium text-emerald-700">Selected: {photoFile.name}</p>}
              </div>

                <label className="sf-label">Password (optional)</label>
                <input
                  type="password"
                  className="sf-input w-full"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                className="sf-btn-secondary flex-1"
                onClick={() => setStep(1)}
                disabled={saving}
              >
                Back
              </button>

              <button
                className="sf-btn-primary flex-1"
                onClick={createCustomer}
                disabled={saving}
              >
                {saving ? "Creating..." : "Create Customer"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
